import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { useOriginProduct } from "@/hooks/useOriginProduct";
import { FootprintProduct } from "@/types/results";

interface DistributionHydricChartData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  breakdown?: {
    materials?: number;
    energy?: number;
    water?: number;
  };
}

interface DistributionHydricPieChartProps {
  product: FootprintProduct;
  className?: string;
}

export const DistributionHydricPieChart = ({
  product,
  className,
}: DistributionHydricPieChartProps) => {
  const { t } = useLanguage();

  // Fetch origin product data if needed (for transformation products from studied quarries)
  const { originProduct } = useOriginProduct(
    product.origin_company_id || null,
    product.origin_product_id || null
  );

  // Generate chart data based on activity type and distribution data
  const chartData = useMemo((): DistributionHydricChartData[] => {
    const data: DistributionHydricChartData[] = [];
    
    // Get distribution data from product
    const distribution = product.distribution;
    if (!distribution) return [];

    const activityType = product.product_flow || "extraction";
    const annualProduction = product.annual_production || 0;

    // Get distribution footprint - these are per-unit values, multiply by annual production
    const distributionFootprint = (distribution.hydric_footprint?.total_m3 || 0) * annualProduction;

    // Check if this product comes from an already studied quarry
    const hasStoredOriginData = product.origin_extraction_hydric_footprint_total !== undefined;
    const comesFromStudiedQuarry = product.comes_from_associated === "yes" && 
                                  (hasStoredOriginData || originProduct);

    if (activityType === "extraction") {
      // Extraction / Extraction and Transformation (Mixed) Company
      // For extraction products, materials + electricity + water represents the extraction process
      const extractionFootprint = (product.hydric_footprint_materials || 0) + 
                                 (product.hydric_footprint_electricity || 0) + 
                                 (product.hydric_footprint_water || 0);
      const extractionFootprintTotal = extractionFootprint * annualProduction;
      const totalDisplayed = extractionFootprintTotal + distributionFootprint;

      if (totalDisplayed > 0) {
        if (extractionFootprintTotal > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.extraction"),
            value: extractionFootprintTotal,
            percentage: (extractionFootprintTotal / totalDisplayed) * 100,
            color: "#10b981",
            breakdown: {
              materials: (product.hydric_footprint_materials || 0) * annualProduction,
              energy: (product.hydric_footprint_electricity || 0) * annualProduction,
              water: (product.hydric_footprint_water || 0) * annualProduction,
            },
          });
        }
        
        if (distributionFootprint > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.distribution"),
            value: distributionFootprint,
            percentage: (distributionFootprint / totalDisplayed) * 100,
            color: "#06b6d4",
          });
        }
      }
    } else if (activityType === "transformation") {
      // Transformation / Extraction and Transformation (Mixed) Company
      // For transformation products, materials + electricity + water represents the TRANSFORMATION process
      const transformationFootprint = (product.hydric_footprint_materials || 0) + 
                                     (product.hydric_footprint_electricity || 0) + 
                                     (product.hydric_footprint_water || 0);
      const transformationFootprintTotal = transformationFootprint * annualProduction;
      
      const transportFootprint = (product.hydric_footprint_transport || 0) * annualProduction;
      
      // Get extraction footprint - use origin product data if available
      let extractionFootprint = 0;
      if (comesFromStudiedQuarry) {
        if (hasStoredOriginData) {
          extractionFootprint = product.origin_extraction_hydric_footprint_total || 0;
        } else if (originProduct) {
          extractionFootprint = originProduct.hydric_footprint_total || 0;
        }
      }
      
      const totalDisplayed = extractionFootprint + transformationFootprintTotal + transportFootprint + distributionFootprint;

      if (totalDisplayed > 0) {
        if (extractionFootprint > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.extraction"),
            value: extractionFootprint,
            percentage: (extractionFootprint / totalDisplayed) * 100,
            color: "#14b8a6",
          });
        }
        
        if (transformationFootprintTotal > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.transformation"),
            value: transformationFootprintTotal,
            percentage: (transformationFootprintTotal / totalDisplayed) * 100,
            color: "#8b5cf6",
            breakdown: {
              materials: (product.hydric_footprint_materials || 0) * annualProduction,
              energy: (product.hydric_footprint_electricity || 0) * annualProduction,
              water: (product.hydric_footprint_water || 0) * annualProduction,
            },
          });
        }
        
        if (transportFootprint > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.transport"),
            value: transportFootprint,
            percentage: (transportFootprint / totalDisplayed) * 100,
            color: "#f59e0b",
          });
        }
        
        if (distributionFootprint > 0) {
          data.push({
            name: t("results.distribution.hydric.phases.distribution"),
            value: distributionFootprint,
            percentage: (distributionFootprint / totalDisplayed) * 100,
            color: "#06b6d4",
          });
        }
      }
    }

    return data;
  }, [product, originProduct, t]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg max-w-xs">
          <p className="text-sm font-semibold mb-2 text-gray-900">{data.name}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.hydric.units.m3")}:
              </span>
              <span className="text-sm font-medium">
                {formatNumber(data.value)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.hydric.units.percentage")}:
              </span>
              <span className="text-sm font-medium">
                {data.percentage.toFixed(1)}%
              </span>
            </div>
            {data.breakdown && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2 font-medium">
                  {t("results.distribution.hydric.breakdown.title")}:
                </p>
                {data.breakdown.materials !== undefined && data.breakdown.materials > 0 && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.distribution.hydric.breakdown.materials")}:
                    </span>
                    <span className="text-xs font-medium">
                      {formatNumber(data.breakdown.materials)} m³
                    </span>
                  </div>
                )}
                {data.breakdown.energy !== undefined && data.breakdown.energy > 0 && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.distribution.hydric.breakdown.energy")}:
                    </span>
                    <span className="text-xs font-medium">
                      {formatNumber(data.breakdown.energy)} m³
                    </span>
                  </div>
                )}
                {data.breakdown.water !== undefined && data.breakdown.water > 0 && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.distribution.hydric.breakdown.water")}:
                    </span>
                    <span className="text-xs font-medium">
                      {formatNumber(data.breakdown.water)} m³
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: { payload?: Array<{ color: string; value: string }> }) => {
    if (!payload) return null;
    
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-600">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (chartData.length === 0) {
    const activityType = product.product_flow || "extraction";
    const isTransformationProduct = activityType === "transformation";
    
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {t("results.distribution.hydric.chart.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            {isTransformationProduct ? (
              <div className="space-y-2">
                <p className="text-gray-500">
                  {t("results.distribution.hydric.chart.transformationNoData")}
                </p>
                <p className="text-sm text-gray-400">
                  {t("results.distribution.hydric.chart.transformationNoDataHint")}
                </p>
              </div>
            ) : (
              <p className="text-gray-500">
                {t("results.distribution.hydric.chart.noData")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">
          {t("results.distribution.hydric.chart.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={40}
                paddingAngle={2}
                dataKey="value"
                animationDuration={1000}
                minAngle={1}
                label={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
